/**
 * Shared utility for formatting error messages into user-friendly text
 * Used across authentication service and UI components
 */

/**
 * Escapes special regex characters in a string to prevent ReDoS attacks
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Error code mappings for user-friendly messages
 */
const ERROR_CODE_MAP: Record<string, string> = {
  'Account Error': 'We were unable to retrieve your account information. Please try again in a few moments. If the problem persists, please contact support for assistance.',
  'ACCOUNT_ERROR': 'We were unable to retrieve your account information. Please try again in a few moments. If the problem persists, please contact support for assistance.',
  'ACCOUNT_NOT_FOUND': 'No account found for this user. Please contact support or try a different account.',
  'ACCOUNT_LOCKED': 'Your account is temporarily locked. Please contact support for assistance.',
  'ACCOUNT_INACTIVE': 'Your account is inactive. Please contact support to reactivate your account.',
}

/**
 * Formats account error messages into user-friendly text
 * Handles cases where the backend returns errorCode and errorMessage in the response body
 * 
 * @param errorCode - The error code from the backend
 * @param errorMessage - Optional error message from the backend
 * @returns User-friendly error message
 */
export function formatAccountErrorMessage(errorCode: string, errorMessage?: string | null): string {
  // If we have a mapped message, use it
  if (ERROR_CODE_MAP[errorCode]) {
    return ERROR_CODE_MAP[errorCode]
  }

  // Try to extract a user-friendly message from the errorMessage
  if (errorMessage) {
    // Remove "Error: " prefix if present
    let cleanMessage = errorMessage.replace(/^Error:\s*/i, '')
    
    // Extract description if it follows "Description: " pattern
    const descriptionMatch = cleanMessage.match(/Description:\s*(.+)/i)
    if (descriptionMatch && descriptionMatch[1]) {
      cleanMessage = descriptionMatch[1].trim()
    }

    // Remove redundant error code prefixes (escape errorCode to prevent ReDoS)
    const escapedErrorCode = escapeRegex(errorCode)
    cleanMessage = cleanMessage.replace(new RegExp(`^${escapedErrorCode}:\\s*`, 'i'), '')

    // If the message is still meaningful, use it
    if (cleanMessage && cleanMessage.length > 0 && !cleanMessage.match(/^\{.*\}$/)) {
      // Don't use if it looks like raw JSON
      return cleanMessage
    }
  }

  // Fallback to generic message
  return 'We encountered an issue while processing your account. Please try again in a few moments. If the problem persists, please contact support for assistance.'
}

/**
 * Formats error messages that might contain JSON or poorly formatted text
 * Extracts user-friendly messages from error strings
 * 
 * @param errorMessage - The raw error message that may contain JSON or be poorly formatted
 * @returns Cleaned, user-friendly error message
 */
export function formatErrorMessage(errorMessage: string): string {
  // If the message looks like raw JSON, try to extract useful information
  if (errorMessage.includes('{') && errorMessage.includes('errorCode')) {
    try {
      // Try to extract JSON from the message (handle multiline with [\s\S])
      const jsonMatch = errorMessage.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const errorData = JSON.parse(jsonMatch[0])
        if (errorData.errorCode && errorData.errorMessage) {
          // Use the shared formatter for consistency
          return formatAccountErrorMessage(errorData.errorCode, errorData.errorMessage)
        }
      }
    } catch (parseError) {
      // If parsing fails, continue with original message cleanup
    }
  }
  
  // Remove "Network error during authorization: " prefix if present
  let cleaned = errorMessage.replace(/^Network error during authorization:\s*/i, '')
  
  // If it still looks like JSON, provide a generic message
  if (cleaned.includes('{') && cleaned.includes('errorCode')) {
    return 'We encountered an issue while processing your account. Please try again in a few moments. If the problem persists, please contact support for assistance.'
  }
  
  return cleaned
}
